/**
 * Soft wrapper around the experimental Navigator Audio Session API.
 * On supporting browsers (notably recent Safari/iOS), this controls whether
 * our page interrupts background music (Spotify, Apple Music, etc.).
 *
 * Call *capture* uses a PlayAndRecord epoch. Incoming *ringtone* must NOT use
 * that epoch — on iPhone, PlayAndRecord often routes to the earpiece so the
 * ring is effectively silent. Ring with transient/playback (speaker) instead,
 * then begin the capture epoch only on Answer/Start.
 *
 * @param {'auto'|'playback'|'transient'|'transient-solo'|'ambient'|'play-and-record'} type
 * @returns {boolean} true if the type was applied
 */

/** Types that make WebKit reject getUserMedia({ audio: true }). */
const CAPTURE_INCOMPATIBLE_TYPES = new Set([
  'playback',
  'transient',
  'transient-solo',
  'ambient',
]);

/** True only while mic/camera capture / connected call owns the session. */
let callAudioEpochActive = false;

export const isCallAudioEpochActive = () => callAudioEpochActive;

/** @deprecated use isCallAudioEpochActive */
export const isPlayAndRecordAudioSessionHeld = () => callAudioEpochActive;

export const setAudioSessionType = (type) => {
  if (typeof navigator === 'undefined') return false;
  try {
    const session = navigator.audioSession;
    if (!session || typeof type !== 'string') return false;

    if (callAudioEpochActive && CAPTURE_INCOMPATIBLE_TYPES.has(type)) {
      return false;
    }

    session.type = type;
    return true;
  } catch (_) {
    return false;
  }
};

/** Mix with other apps — use for muted video / silent unlock. */
export const setAmbientAudioSession = () => setAudioSessionType('ambient');

/** Exclusive media playback — use when the page should own audio (unmuted video). */
export const setPlaybackAudioSession = () => setAudioSessionType('playback');

/** Short alerts that may duck other audio — banners / rings. */
export const setTransientAudioSession = () => setAudioSessionType('transient');

/**
 * Incoming/outgoing ringtone audio routing.
 * Prefer `playback` so iPhone rings even when the hardware mute switch is on
 * (ambient/transient follow the mute switch; playback does not).
 * After Answer/Start, capture epoch forces PlayAndRecord instead.
 */
export const setRingtoneAudioSession = () => {
  if (callAudioEpochActive) {
    return setAudioSessionType('play-and-record');
  }
  if (setAudioSessionType('playback')) return true;
  return setAudioSessionType('transient');
};

/** One-shot PlayAndRecord (no epoch). Prefer begin/end for calls. */
export const setPlayAndRecordAudioSession = () => setAudioSessionType('play-and-record');

/** Idempotent: enter/keep call audio mode for Answer/Start → connected → hangup. */
export const beginCallAudioEpoch = () => {
  callAudioEpochActive = true;
  return setAudioSessionType('play-and-record');
};

/** Leave call audio mode (hangup / reject). Safe to call multiple times. */
export const endCallAudioEpoch = () => {
  callAudioEpochActive = false;
  return setAudioSessionType('ambient');
};

export const reassertPlayAndRecordAudioSession = () => {
  if (!callAudioEpochActive) return false;
  return setAudioSessionType('play-and-record');
};

/** @deprecated use beginCallAudioEpoch */
export const acquirePlayAndRecordAudioSession = () => beginCallAudioEpoch();

/** @deprecated use endCallAudioEpoch */
export const releasePlayAndRecordAudioSession = () => endCallAudioEpoch();
